import { alignBitmap, qrMatrixToBitmap } from './bitmap.js';
import { isEncodable } from './codepages.js';
import { EscPosEncoder } from './encoder.js';
import { layoutDocument } from './layout.js';
import { normalizeProfile } from './profile.js';

/**
 * Document → ESC/POS bytes for one printer profile.
 *
 * Environment hooks (all optional, so the same code runs in the browser and the print agent):
 *   images        { logo: bitmap, … } for { t: 'image', name } blocks (see bitmap.js)
 *   rasterizeText (row, profile) → bitmap; used for rows the code page can't print (₹, Telugu,
 *                 Hindi…) when profile.rasterText is on. Without it those rows fall back to text.
 *   qrMatrix      (data) → { size, get(x, y) }; enables qrMode 'raster'
 *   openDrawer    pulse the cash drawer first (only if the profile has a drawer)
 *   copies        overrides profile.copies
 */
export function renderEscPos(doc, profile, opts = {}) {
  const p = normalizeProfile(profile);
  const canRaster = Boolean(p.rasterText && typeof opts.rasterizeText === 'function');
  const rows = layoutDocument(doc, p, { rasterText: canRaster });
  const copies = Math.max(1, Math.min(5, opts.copies ?? p.copies));
  const one = encodeRows(rows, p, { ...opts, canRaster, openDrawer: false });
  const parts = [];
  if (opts.openDrawer && p.drawer.enabled) parts.push(new EscPosEncoder(p).drawer(p.drawer.pin).bytes());
  for (let i = 0; i < copies; i++) parts.push(one);
  return concatBytes(parts);
}

export function encodeRows(rows, p, { images = {}, rasterizeText, qrMatrix, canRaster = false } = {}) {
  const enc = new EscPosEncoder({ codePage: p.codePage }).initialize().density(p.density);
  let state = { w: 1, h: 1, bold: false, invert: false, underline: false };
  const setStyle = (r) => {
    if (r.w !== state.w || r.h !== state.h) enc.size(r.w, r.h);
    if (r.bold !== state.bold) enc.bold(r.bold);
    if (r.invert !== state.invert) enc.invert(r.invert);
    if (r.underline !== state.underline) enc.underline(r.underline);
    state = { w: r.w, h: r.h, bold: r.bold, invert: r.invert, underline: r.underline };
  };
  const plain = { w: 1, h: 1, bold: false, invert: false, underline: false };

  for (const r of rows) {
    if (r.k === 'text') {
      if (canRaster && !isEncodable(r.text, p.codePage)) {
        const bmp = rasterizeText(r, p);
        if (bmp) {
          setStyle(plain);
          enc.image(alignBitmap(bmp, p.dotWidth, 'left'));
          continue;
        }
      }
      setStyle(r);
      enc.text(r.invert ? r.text : r.text.replace(/ +$/, '')).newline();
    } else if (r.k === 'image') {
      const bmp = r.bitmap ?? images[r.name];
      if (!bmp || bmp.width > p.dotWidth) continue;
      setStyle(plain);
      enc.image(alignBitmap(bmp, p.dotWidth, r.align));
    } else if (r.k === 'qr') {
      if (p.qrMode === 'off') continue;
      setStyle(plain);
      if (p.qrMode === 'raster' && typeof qrMatrix === 'function') {
        const m = qrMatrix(r.data);
        const scale = Math.max(1, Math.min(r.module, Math.floor(p.dotWidth / (m.size + 4))));
        enc.image(alignBitmap(qrMatrixToBitmap(m, scale), p.dotWidth, r.align));
      } else {
        enc.align(r.align).qr(r.data, { size: r.module }).newline().align('left');
      }
    } else if (r.k === 'barcode') {
      setStyle(plain);
      enc
        .align('center')
        .barcode(r.data, { width: p.dotWidth < 500 ? 2 : 3 })
        .newline()
        .align('left');
    }
  }
  setStyle(plain);
  enc.feed(p.feedLines);
  if (p.cut !== 'none') enc.cut(p.cut);
  return enc.bytes();
}

export function concatBytes(parts) {
  const total = parts.reduce((s, x) => s + x.length, 0);
  const out = new Uint8Array(total);
  let o = 0;
  for (const x of parts) {
    out.set(x, o);
    o += x.length;
  }
  return out;
}

/** Hex dump (16 bytes per line) — used for snapshot tests and debugging printers. */
export function hexDump(bytes) {
  const lines = [];
  for (let i = 0; i < bytes.length; i += 16) {
    const chunk = Array.from(bytes.subarray(i, i + 16));
    const hex = chunk.map((b) => b.toString(16).padStart(2, '0')).join(' ');
    const ascii = chunk.map((b) => (b >= 0x20 && b < 0x7f ? String.fromCharCode(b) : '.')).join('');
    lines.push(`${i.toString(16).padStart(6, '0')}  ${hex.padEnd(47)}  ${ascii}`);
  }
  return lines.join('\n');
}

/** Base64 helpers that work in browsers and Node without Buffer. */
export function bytesToBase64(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

export function base64ToBytes(b64) {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}
